<!--
  Muestra el icono con la paleta de su aplicación para conservar la misma identidad en el
  panel y la navegación.

  El contenedor declara data-palette y hereda el modo claro u oscuro de quien lo utiliza.
  Los tokens de compat.css se calculan en ese mismo contenedor.
-->
<script lang="ts">
  import type { AppTheme } from "@shared/types";

  import { appsLook } from "../../lib/appsLook.svelte";
  import { paletteAttrs } from "../../lib/appTheme";
  import { cx } from "../../lib/cx";
  import Icon from "../Icon.svelte";

  let {
    app,
    size = 24,
    class: className,
    onclick,
    tip,
    ariaLabel,
  }: {
    app: { icon: string; theme?: AppTheme | null };
    size?: number;
    class?: string;
    /** Si viene, la pastilla se vuelve un botón: cursor, foco y accesible. */
    onclick?: () => void;
    /** El globo de ayuda. Lo dibuja `TooltipLayer`, no la pastilla. */
    tip?: string;
    ariaLabel?: string;
  } = $props();

  const base = $derived(
    cx(
      onclick ? "btn-open-app icon-app" : "icon-app-static",
      "app-icon",
      "flex shrink-0 items-center justify-center",
      onclick && "app-icon-action",
      className ?? "app-icon-default",
    ),
  );
</script>

{#if onclick}
  <button
    type="button"
    {onclick}
    data-tip={tip}
    aria-label={ariaLabel}
    class={base}
    {...paletteAttrs(appsLook.theme(app.theme), { fontScale: false })}
  >
    <Icon name={app.icon} {size} />
  </button>
{:else}
  <span class={base} {...paletteAttrs(appsLook.theme(app.theme), { fontScale: false })}>
    <Icon name={app.icon} {size} />
  </span>
{/if}

<style>
  /* La pastilla lleva los colores de la aplicación, no los del panel. */
  .app-icon {
    border-radius: var(--radius-sm);
    background: color-mix(in oklab, var(--accent) 20%, transparent);
    color: var(--accent-soft-text);
  }

  .app-icon-default {
    width: 1.75rem;
    height: 1.75rem;
  }

  .app-icon-action {
    cursor: pointer;
    transition:
      background-color 150ms,
      color 150ms;

    &:hover {
      background: color-mix(in oklab, var(--accent) 30%, transparent);
    }
  }
</style>
